import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { WHATS_NEW_COLORS } from 'utils/whatsNew';

/**
 * "N new tools are now available" banner on the Tools tab (Figma 405:410).
 *
 * Web port of the desktop banner. Per dev note 405:432 it appears only while a
 * release batch is unread and undismissed, "Explore new tools" opens What's New
 * filtered to new tools, and x hides THAT batch permanently for this user.
 *
 * `LogoComponent` is injected because the cloud dashboard defines ToolLogo
 * inline; passing it avoids a circular import.
 */
export default function WhatsNewBanner({ banner, apps, onExplore, onDismiss, LogoComponent }) {
  if (!banner) {
    return null;
  }

  const newToolCount = Number(banner.new_tool_count) || 0;
  const updateCount = Number(banner.update_count) || 0;

  const headline =
    newToolCount > 0
      ? `${newToolCount} new tool${newToolCount === 1 ? '' : 's'} ${newToolCount === 1 ? 'is' : 'are'} now available`
      : `${updateCount} product update${updateCount === 1 ? '' : 's'} available`;

  const bannerApps = Array.isArray(apps) ? apps : [];

  return (
    <Box
      sx={{
        mx: 2.5,
        px: { xs: 1.75, md: 2.5 },
        py: 1.75,
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'center',
        gap: { xs: 1.25, md: 2 },
        borderRadius: '12px',
        backgroundColor: WHATS_NEW_COLORS.bannerBg,
        border: `1px solid ${WHATS_NEW_COLORS.bannerBorder}`
      }}
    >
      {bannerApps.length > 0 && LogoComponent ? (
        <Stack direction="row" sx={{ flexShrink: 0 }}>
          {bannerApps.map((app, index) => (
            <LogoComponent key={app?.id ?? `banner_${index}`} app={app} size={40} radius={10} sx={index === 0 ? undefined : { ml: -0.5 }} />
          ))}
        </Stack>
      ) : null}

      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 600, color: 'white' }}>{headline}</Typography>
        <Typography sx={{ fontSize: 13, color: '#aeb6c2' }}>Discover the latest additions to KLOOW.</Typography>
      </Stack>

      <Box
        component="button"
        type="button"
        onClick={onExplore}
        sx={{
          width: { xs: '100%', md: 184 },
          height: 42,
          flexShrink: 0,
          borderRadius: '9px',
          fontSize: 13,
          fontWeight: 600,
          color: 'white',
          cursor: 'pointer',
          outline: 'none',
          backgroundColor: WHATS_NEW_COLORS.bannerCtaBg,
          border: `1px solid ${WHATS_NEW_COLORS.bannerCtaBorder}`,
          transition: 'background-color 140ms ease',
          '&:hover': { backgroundColor: '#1a2336' },
          '&:focus-visible': { boxShadow: '0 0 0 2px rgba(112, 141, 255, 0.5)' }
        }}
      >
        Explore new tools →
      </Box>

      <Tooltip title="Dismiss">
        <IconButton onClick={onDismiss} aria-label="Dismiss new tools banner" sx={{ color: '#aeb6c2', flexShrink: 0, '&:hover': { color: 'white' } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
